import CustomerWorkspaceShell from 'src/common/customerSharing/CustomerWorkspaceShell';
import CustomerContext from 'src/common/customerSharing/CustomerContext';
import AiPersonMemory from 'src/features/AiChannel/AiPersonMemory';
import AiConversation from 'src/features/AiChannel/AiConversation';
import { CustomerProfileButton } from "src/common/customerSharing/CustomerSharing";
import React, { useState, useEffect } from "react";
import {
  Activity,
  FileText,
  Maximize2,
  Minimize2,
  Plus,
  Minus,
  Tag,
  UserRound,
  X,
  ChevronDown,
} from "lucide-react";
import ContactStats from "./component/ContactStates";
import AssignmentSection from "./component/AssignmentSection";
import ConversationMode from "./component/ConversationMode";
import BotSelector from "./component/BotSelector";
import LabelsSection from "./component/LabelsSection";
import ContactTagsSection from "src/features/Contact/component/ContactTagsSection";
import NotesSection from "./component/NotesSection";
import QuickActions from "./component/QuickActions";
import RuntimeHistory from "./component/RuntimeHistory";
import useConversationRuntime from "./hooks/useConversationRuntime";

function ContactSidebar({ conversation, isOpen, onClose }) {
  const [aiOwnership, setAiOwnership] = useState(null);
  const [customerTab, setCustomerTab] = useState("sales");
  const [salesVisited, setSalesVisited] = useState(true);
  const [aiVisited, setAiVisited] = useState(false);
  const [emailVisited, setEmailVisited] = useState(false);
  useEffect(() => { setCustomerTab("sales"); setSalesVisited(true); setAiVisited(false); setEmailVisited(false); setAiOwnership(null); }, [conversation?.id]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsCollapsed(false);
      setIsMaximized(false);
    }
  }, [isOpen]);

  const [expandedSections, setExpandedSections] = useState({
    assignment: false,
    control: false,
    labelsNotes: false,
    quickActions: false,
    runtime: false,
  });

  const {
    users,
    usersError,
    usersLoading,
    canAssign,
    actionLoading,
    bots,
    assignedUser,
    mode,
    selectedBot,
    labels,
    note,
    runtimeHistory,
    conversationHistory,
    loading,
    assignUser,
    changeMode,
    changeBot,
    pause,
    resume,
    reset,
    restart,
    closeConversation,
    addLabel,
    removeLabel,
    saveNote,
    refreshRuntime,
  } = useConversationRuntime(conversation);

  if (!isOpen) {
    return null;
  }

  const contact = conversation?.contact || {};
  const contactName =
    contact?.name ||
    conversation?.sender_name ||
    conversation?.sender_number ||
    "Unknown Contact";
  const phoneNumber =
    contact?.phone_number || conversation?.sender_number || "-";

  /* --------------------------------
     Section Toggle
  --------------------------------- */

  const toggleSection = (section) => {
    setExpandedSections((previous) => ({
      ...previous,
      [section]: !previous[section],
    }));
  };

  /* --------------------------------
     Expand / Collapse
  --------------------------------- */

  const expandSidebar = () => {
    setIsCollapsed(false);
    setIsMaximized(false);
  };

  const collapseSidebar = () => {
    setIsCollapsed(true);
    setIsMaximized(false);
  };

  /* --------------------------------
     Maximize / Restore
  --------------------------------- */

  const toggleMaximize = () => {
    setIsCollapsed(false);
    setIsMaximized((previous) => !previous);
  };

  /* --------------------------------
     Sidebar Width
  --------------------------------- */

  const sidebarWidth = isMaximized
    ? "md:w-[calc(100%-24px)] md:max-w-none"
    : isCollapsed
      ? "md:w-[72px]"
      : "md:w-[420px] md:max-w-[92vw] xl:w-[440px]";

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-900/10 dark:bg-black/30 ${!isMaximized ? "xl:hidden" : ""}`}
        onClick={onClose}
      />
      <aside
        className={`fixed inset-0 z-50 flex flex-col overflow-hidden border border-[#dce7e6] bg-white transition-[width] duration-300 ease-out md:absolute md:inset-y-2 md:left-auto md:right-2 md:rounded-[22px] md:shadow-[-18px_0_45px_rgba(15,43,66,0.13)] dark:border-dark-border dark:bg-dark-card dark:shadow-[-16px_0_40px_rgba(0,0,0,0.25)] ${sidebarWidth} ${!isMaximized ? "xl:relative xl:inset-auto xl:shrink-0 xl:rounded-none xl:shadow-none xl:border-y-0 xl:border-r-0" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        {isCollapsed ? (
          <div className="flex h-full flex-col items-center gap-2 bg-[#f8faf9] px-2 py-3 dark:bg-dark-card">
            {/* Expand */}
            <SidebarControlButton
              onClick={expandSidebar}
              title="Expand sidebar"
              icon={Plus}
            />

            {/* Maximize */}
            <SidebarControlButton
              onClick={toggleMaximize}
              title="Maximize sidebar"
              icon={Maximize2}
            />

            {/* Close */}
            <SidebarControlButton
              onClick={onClose}
              title="Close"
              icon={X}
              danger
            />
          </div>
        ) : (
          <CustomerWorkspaceShell compact name={contactName} company={contact.company} email={contact.email} phone={phoneNumber} view={customerTab}
            onViewChange={key => { setCustomerTab(key); if (key === 'sales') setSalesVisited(true); if (key === 'ai') setAiVisited(true); if (key === 'email') setEmailVisited(true); }}
            actions={
              <div className="flex items-center gap-1.5">
                {/* Collapse */}
                <SidebarControlButton
                  onClick={collapseSidebar}
                  title="Collapse sidebar"
                  icon={Minus}
                />

                {/* Maximize / Restore */}
                <SidebarControlButton
                  onClick={toggleMaximize}
                  title={isMaximized ? "Restore sidebar" : "Maximize sidebar"}
                  icon={isMaximized ? Minimize2 : Maximize2}
                />

                {/* Close */}
                <SidebarControlButton
                  onClick={onClose}
                  title="Close"
                  icon={X}
                  danger
                />
              </div>
            }>

            <div className="min-h-0 flex-1 overflow-y-auto bg-[#f6f9f8] dark:bg-dark-card">
              <div className="space-y-2.5 px-4 py-4 sm:px-5">
                {customerTab === 'meetings' && <CustomerContext recordId={contact.id || conversation?.whatsapp_contact_id} initialSection="meetings" compact />}
                {emailVisited && <div hidden={customerTab !== 'email'}><CustomerContext key={contact.id || conversation?.whatsapp_contact_id} recordId={contact.id || conversation?.whatsapp_contact_id} initialSection="email" compact /></div>}
                {customerTab === 'calls' && <CustomerContext recordId={contact.id || conversation?.whatsapp_contact_id} initialSection="calls" compact autoOpenSingle />}
                {customerTab === 'whatsapp' && <CustomerContext recordId={contact.id || conversation?.whatsapp_contact_id} name={contactName} initialSection="whatsapp" compact autoOpenSingle />}
                <div hidden={customerTab !== "profile"}>
                {/* Stats */}
                <ContactStats conversation={conversation} contact={contact} />
                <CustomerProfileButton recordId={contact.id || conversation?.whatsapp_contact_id} name={contactName} onOpen={() => { setIsCollapsed(false); setIsMaximized(true); setCustomerTab("sales"); setSalesVisited(true); }} />

                </div>
                {salesVisited && <div hidden={customerTab !== "sales"}><CustomerContext key={contact.id || conversation?.whatsapp_contact_id} recordId={contact.id || conversation?.whatsapp_contact_id} name={contactName} initialSection="crm" compact /></div>}
                <div hidden={customerTab !== "ai"}>
                {aiVisited && <AiConversation key={conversation?.id} conversationId={conversation?.id} onControl={refreshRuntime} onConnection={setAiOwnership} />}
                {aiOwnership === true && <AiPersonMemory key={conversation?.id} conversationId={conversation?.id} refreshVersion={conversation?.last_message_at} />}

                </div>
                <div hidden={customerTab !== "profile"}>
                {/* Assignment */}
                <SidebarSection
                  icon={UserRound}
                  title="Assignment"
                  expanded={expandedSections.assignment}
                  onToggle={() => toggleSection("assignment")}
                >
                  <AssignmentSection
                    canAssign={canAssign}
                    error={usersError}
                    users={users}
                    assignedUser={assignedUser}
                    loading={
                      loading || usersLoading || actionLoading === "assign-user"
                    }
                    onAssignUser={assignUser}
                  />
                </SidebarSection>

                {/* Existing local controls only apply to unconnected workspaces. */}
                {aiOwnership === false && <SidebarSection
                  icon={Activity}
                  title="Conversation Control"
                  expanded={expandedSections.control}
                  onToggle={() => toggleSection("control")}
                >
                  <div className="space-y-3">
                    <ConversationMode
                      mode={mode}
                      loading={loading}
                      onModeChange={changeMode}
                    />

                    {mode === "Bot" && (
                      <BotSelector
                        bots={bots}
                        selectedBot={selectedBot}
                        loading={loading}
                        onBotChange={changeBot}
                      />
                    )}
                  </div>
                </SidebarSection>}

                {/* Tags */}
                <SidebarSection
                  icon={Tag}
                  title={aiOwnership ? "Inbox tags" : "Tags"}
                  expanded={expandedSections.labels}
                  onToggle={() => toggleSection("labels")}
                >
                  <div className="space-y-3">
                    <ContactTagsSection contact={contact} />
                    <LabelsSection
                      labels={labels}
                      loading={loading}
                      onAddLabel={addLabel}
                      onRemoveLabel={removeLabel}
                    />
                  </div>
                </SidebarSection>

                {/* Legacy notes remain available in unconnected workspaces. */}
                {aiOwnership === false && <SidebarSection
                  icon={FileText}
                  title="Notes"
                  expanded={expandedSections.notes}
                  onToggle={() => toggleSection("notes")}
                >
                  <NotesSection
                    note={note}
                    loading={loading}
                    onNoteSave={saveNote}
                  />
                </SidebarSection>}
                </div>
                <div hidden={customerTab !== "activity"}>
                {/* Quick Actions */}
                <SidebarSection
                  icon={Activity}
                  title="Quick Actions"
                  expanded={expandedSections.quickActions}
                  onToggle={() => toggleSection("quickActions")}
                >
                  <QuickActions
                    conversation={conversation}
                    loading={loading}
                    onPause={pause}
                    onResume={resume}
                    onReset={reset}
                    onRestart={restart}
                    onClose={closeConversation}
                  />
                </SidebarSection>

                {/* Runtime History */}
                <SidebarSection
                  icon={Activity}
                  title="Runtime History"
                  expanded={expandedSections.runtime}
                  onToggle={() => toggleSection("runtime")}
                >
                  <RuntimeHistory
                    history={runtimeHistory}
                    loading={loading}
                    onRefresh={refreshRuntime}
                    rawHistory={conversationHistory}
                  />
                </SidebarSection>
                </div>
              </div>
            </div>

            <div className="shrink-0 border-t border-[#e2eae8] bg-white/90 px-4 py-3 dark:border-dark-border dark:bg-dark-card">
              <div className="flex items-center justify-between gap-3">
                <span className="shrink-0 text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#89969D]">
                  Conversation ID
                </span>

                <span className="truncate rounded-[8px] bg-[#f2f6f5] px-2 py-1 font-mono text-[9px] text-[#7A878E] dark:bg-dark-surface dark:text-dark-text-muted">
                  {conversation?.id || "-"}
                </span>
              </div>
            </div>
          </CustomerWorkspaceShell>
        )}
      </aside>
    </>
  );
}

function SidebarControlButton({ icon: Icon, onClick, title, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border transition-all duration-200 active:scale-95
        ${
          danger
            ? `
              border-[#E3E8EB]
              bg-white
              text-[#78858C]
              hover:border-red-200
              hover:bg-red-50
              hover:text-red-500
              dark:border-dark-border
              dark:bg-dark-surface
            `
            : `
              border-[#DDE5E9]
              bg-white
              text-[#65747C]
              hover:border-[#bfd7d2]
              hover:bg-[#edf7f3]
              hover:text-[#27856d]
              dark:border-dark-border
              dark:bg-dark-surface
            `
        }
      `}
    >
      <Icon size={14} strokeWidth={1.9} />
    </button>
  );
}

function SidebarSection({ icon: Icon, title, expanded, onToggle, children }) {
  return (
    <section
      className={`min-w-0 rounded-[13px] border bg-white transition ${expanded ? "border-[#d9e6e2] shadow-[0_5px_14px_rgba(15,43,66,0.04)]" : "border-transparent"}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-[12px] px-2.5 py-2.5 transition-colors hover:bg-[#edf7f3] dark:hover:bg-dark-soft-blue"
      >
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] border border-[#dce8e4] bg-[#f2f7f5] text-[#527a70] dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary">
            <Icon size={14} strokeWidth={1.8} />
          </div>

          <span className="truncate text-[11px] font-bold text-[#536a75] dark:text-dark-text-secondary">
            {title}
          </span>
        </div>

        <ChevronDown
          size={14}
          strokeWidth={1.8}
          className={`shrink-0 text-text-muted transition-transform duration-200 dark:text-dark-text-muted ${
            expanded ? "rotate-180 text-[#27856d] dark:text-dark-primary" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="min-w-0 border-t border-[#eef3f1] px-2 pb-2.5 pt-2 dark:border-dark-border">
          {children}
        </div>
      )}
    </section>
  );
}

export default ContactSidebar;
